import type { DirectiveBinding } from 'vue'
import gsap from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import defu from 'defu'

if (typeof window !== 'undefined') {
  gsap.registerPlugin(ScrollTrigger)
}

const checkReducedMotion = () => window.matchMedia(`(prefers-reduced-motion: reduce)`) === true || window.matchMedia(`(prefers-reduced-motion: reduce)`).matches === true

const defaults: Record<string, (el: HTMLElement) => any> = {
  'in': (el: HTMLElement) => ({
    opacity: 0,
    duration: 0.4,
    scrollTrigger: {
      trigger: el,
      start: 'top 90%',
      end: 'bottom top',
    },
  }),
  'in-from-bottom': (el: HTMLElement) => ({
    y: 30,
    opacity: 0,
    duration: 0.4,
    scrollTrigger: {
      trigger: el,
      start: 'top 90%',
      end: 'bottom top',
    },
  }),
  'in-from-top': (el: HTMLElement) => ({
    y: -30,
    opacity: 0,
    duration: 0.4,
    scrollTrigger: {
      trigger: el,
      start: 'top 90%',
      end: 'bottom top',
    },
  }),
  'in-from-left': (el: HTMLElement) => ({
    x: -30,
    opacity: 0,
    duration: 0.4,
    scrollTrigger: {
      trigger: el,
      start: 'top 90%',
      end: 'bottom top',
    },
  }),
  'in-from-right': (el: HTMLElement) => ({
    x: 30,
    opacity: 0,
    duration: 0.4,
    scrollTrigger: {
      trigger: el,
      start: 'top 90%',
      end: 'bottom top',
    },
  }),
  'zoom-in': (el: HTMLElement) => ({
    scale: 0.9,
    opacity: 0,
    duration: 0.4,
    scrollTrigger: {
      trigger: el,
      start: 'top 90%',
      end: 'bottom top',
    },
  }),
  'zoom-in-out': (el: HTMLElement) => ({
    scale: 0,
    opacity: 0,
    duration: 0.6,
    ease: 'back.out(1.2)',
    scrollTrigger: {
      trigger: el,
      start: 'top 90%',
      end: 'bottom top',
    },
  }),
}

export default defineNuxtPlugin((nuxtApp) => {
  Object.keys(defaults).forEach((key) => {
    nuxtApp.vueApp.directive(`animate-children-${key}`, {
      getSSRProps() {
        // hide parent element during server-side rendering
        return { class: ['initially-hidden'] }
      },
      created(el: HTMLElement) {
        el.classList.add('initially-hidden')
      },
      mounted(el: HTMLElement, binding: DirectiveBinding) {
        // avoid conflicts if directive is triggered multiple times
        const gsapHookedTargets = gsap.globalTimeline.getChildren(true, true, false).flatMap(child =>
          child.targets().filter(t => !(t instanceof Function)))
        if (Array.from(el.children).filter(c => gsapHookedTargets.includes(c)).length > 0) return

        // when mounted, make parent visible again
        el.classList.remove('initially-hidden')

        if (checkReducedMotion()) {
          return
        }

        const options = defu(
          binding.value ?? {},
          defaults[key] ? defaults[key](el) : {},
          { stagger: 0.1 },
        )

        if (el.children.length === 0) return
        gsap.from(el.children, options)
      },
    })
    nuxtApp.vueApp.directive(`animate-${key}`, {
      getSSRProps() {
        // hide element during server-side rendering
        return { class: ['initially-hidden'] }
      },
      created(el: HTMLElement) {
        el.classList.add('initially-hidden')
      },
      mounted(el: HTMLElement, binding: DirectiveBinding) {
        // avoid conflicts if directive is triggered multiple times
        const gsapHookedTargets = gsap.globalTimeline.getChildren(true, true, false).flatMap(child =>
          child.targets().filter(t => !(t instanceof Function)))
        if (gsapHookedTargets.includes(el)) return

        // when mounted, make element visible again
        el.classList.remove('initially-hidden')

        if (checkReducedMotion()) {
          return
        }

        const options = defu(
          binding.value ?? {},
          defaults[key] ? defaults[key](el) : {},
        )

        gsap.from(el, options)
      },
    })
  })
})
